import type { PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import {
  effectiveYExtent,
  includeZero,
  toCanvasWidth,
  toCanvasX,
  toCanvasY,
} from "./scale-utils";

export type BarRendererStyle = Readonly<{
  fillStyle: string;
  minWidth: number;
}>;

/** Canvas bar renderer for width-in-domain bar payloads. */
export class BarRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: BarRendererStyle = {
    fillStyle: "#2563eb",
    minWidth: 4,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isBarWidthEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isBarWidthEntry(entry)) return;

    const baseExtent = effectiveYExtent(entry, request);
    if (baseExtent === null || baseExtent === undefined) return;
    const extent = includeZero(baseExtent);
    const zeroY = toCanvasY(0, extent, target.size.height);

    target.ctx.fillStyle = this.style.fillStyle;

    for (const [x, value] of entry.series.range(
      request.viewRange.start,
      request.viewRange.end,
    ).points()) {
      const barX = toCanvasX(x, request.viewRange, target.size.width);
      const barWidth = Math.max(
        this.style.minWidth,
        Math.abs(toCanvasWidth(value.width, request.viewRange, target.size.width)),
      );
      const y = toCanvasY(value.y, extent, target.size.height);
      const top = Math.min(y, zeroY);
      const height = Math.abs(y - zeroY);

      target.ctx.fillRect(barX, top, barWidth, height);
    }
  }
}
